// The dashboard (ZENITH.md §18): a widget grid over the sites in the scope picker's
// scope. The arrangement is the person's own, kept per scope, and changes
// only in edit mode; the figures come from one call that reads each cell
// once however many widgets ask.
import { useCallback, useEffect, useId, useMemo, useRef, useState, type RefObject } from "react";
import { api, idempotencyKey, unattended } from "../api/client";
import { SKIP, UNATTENDED, useQuery } from "../api/hooks";
import { useMutation } from "../api/mutation";
import type { components } from "../api/schema";
import { canAnywhere } from "../authz";
import { BriefingCard, useBriefing } from "../dashboard/Briefing";
import Grid from "../dashboard/Grid";
import { clock, plural } from "../dashboard/format";
import { firstFree } from "../dashboard/geometry";
import { CENTERED, WidgetBody } from "../dashboard/widgets";
import { Boundary, WidgetFailed } from "../shell/Boundary";
import { ageOf, useReportedAge } from "../shell/freshness";
import { showsUnfinished } from "../shell/nav";
import { ScopeLink, useScope, type Scope } from "../shell/scope";
import { useSession } from "../shell/session";
import { useShowsVerticals } from "../shell/verticalPreference";
import { Loading, Popover, ProblemBox, TableSkeleton, When } from "../ui";
import { AuditActionCell, AuditActorCell, AuditTargetCell, TargetRef } from "./AuditCells";
import { CloseIcon, Glyph } from "../icons";
import { echoed } from "../api/openEnum";

type Widget = components["schemas"]["DashboardWidget"];
type WidgetType = components["schemas"]["WidgetType"];

/** A widget as the layout's PUT carries it: as read, its type sent back as received (ADR-0511 4a,
 * `echoed`), so a widget a newer release placed is kept rather than dropped. */
function widgetWrite(w: Widget): components["schemas"]["DashboardWidgetWrite"] {
  return { ...w, type: echoed<WidgetType>(w.type) };
}
type Entry = components["schemas"]["DashboardCatalogEntry"];
type WidgetScope = components["schemas"]["WidgetScope"];

/** The narrowest id the scope holds is the dashboard's scope, as the layout key is. */
function dashQuery(scope: Scope): WidgetScope {
  if (scope.siteId) return { site_id: scope.siteId };
  if (scope.customerId) return { customer_id: scope.customerId };
  if (scope.orgId) return { org_id: scope.orgId };
  return {};
}
const scopeKey = (q: WidgetScope) => q.site_id ? "site:" + q.site_id : q.customer_id ? "customer:" + q.customer_id : q.org_id ? "org:" + q.org_id : "all";
const scopeWord = (q: WidgetScope) => q.site_id ? "this site" : q.customer_id ? "this customer" : q.org_id ? "this organization" : "everything you can see";
/** A pinned scope's key as a tooltip says it: the level as a word, then the id.
 * `org` is the key's spelling and not an operator's word. */
const pinnedTo = (key: string) => key === "all" ? "everything you can see" : key.replace(/^org:/, "organization:").replace(":", " ");
const SAVE_DELAY = 600;
const SAVED_FLASH = 1600;
/** The change stream's path (ZENITH.md §17.1; ADR-0240). A literal, and kept
 * one, because tools/uiclientcheck reads every versioned path literal under
 * web/ and compares it with api/openapi.yaml — a path composed at run time
 * would be a client call the contract check cannot see. The scope rides in
 * the query string, which is not part of the path. */
const CHANGES = "/v1/changes";
/** Below this the twelve columns are not honest; the page renders the saved
 * arrangement and does not let it be changed, so a narrow window cannot save
 * a squeezed one. */
export const MIN_GRID_WIDTH = 900;

export default function Overview() {
  const { me } = useSession();
  const { scope } = useScope();
  const query = useMemo(() => dashQuery(scope), [scope]);
  const key = scopeKey(query);
  // Where nothing is saved, the built-in layout is the scope's vertical's only
  // for a person who shows verticals; for anyone else it is the neutral one,
  // on the read and on Reset alike (ADR-0489 decisions 3a, 5a).
  const neutral = !useShowsVerticals();

  const layout = useQuery(() => api.GET("/dashboard/layout", { params: { query: { ...query, neutral } } }), [key, neutral]);
  const [widgets, setWidgets] = useState<Widget[]>();
  const [dirty, setDirty] = useState(0);
  // `widgets` is the draft of the server's document; a new answer replaces the
  // draft. That is state derived from a query answer, set when the answer
  // lands, and the effect is the place that happens.
  // eslint-disable-next-line react-hooks/set-state-in-effect -- the draft follows the server's answer
  useEffect(() => { if (layout.status === "ready") { setWidgets(layout.data.widgets); setDirty(0); } }, [layout.status, layout.data, key]);
  const catalog: Entry[] = layout.data?.catalog ?? [];
  const columns = layout.data?.columns ?? 12;
  const entry = (type: string) => catalog.find((c) => c.type === type);

  // Edit mode, and whether the window is wide enough to offer it.
  const [wantEditing, setEditing] = useState(false);
  // Measured on an element this page renders, not on the shell's column (#202).
  const page = useRef<HTMLDivElement>(null);
  const wide = useWide(page);
  // A narrow window is never in edit mode, whatever was asked for before it shrank.
  const editing = wantEditing && wide;

  // Saving: debounced after the last change, replaced by what the server kept.
  const save = useMutation();
  const [saveState, setSaveState] = useState<"idle" | "saving" | "saved">("idle");
  const pending = useRef<ReturnType<typeof setTimeout>>(undefined);
  useEffect(() => {
    if (!widgets || dirty === 0) return;
    clearTimeout(pending.current);
    pending.current = setTimeout(() => {
      void save.run(() => api.PUT("/dashboard/layout", { params: { query, header: { "Idempotency-Key": idempotencyKey() } }, body: { widgets: widgets.map(widgetWrite) } })
        .then((r) => { if (r.data) setWidgets(r.data.widgets); return r; }))
        .then((ok) => {
          setSaveState(ok ? "saved" : "idle");
          if (ok) pending.current = setTimeout(() => setSaveState("idle"), SAVED_FLASH);
        });
    }, SAVE_DELAY);
    return () => clearTimeout(pending.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- runs once per change (`dirty`); `widgets` and `query` are read when the debounce fires
  }, [dirty]);
  const change = (next: Widget[]) => { setWidgets(next); setDirty((d) => d + 1); setSaveState("saving"); };

  // Figures: one call for every widget on screen, again on the cache's cadence.
  const refs = useMemo(() => (widgets ?? []).map((w) => ({ id: w.id, type: echoed<WidgetType>(w.type), scope: w.scope })), [widgets]);
  const refsKey = JSON.stringify(refs);
  // An empty dashboard asks nothing and holds no figures.
  // A read the page asks for by itself — the cadence, an announcement, the
  // stream reopening — is marked unattended and is not use (ADR-0192 decision
  // 3b): a dashboard left open does not keep its session alive. The person's
  // own asks — the Refresh button, coming back to the tab — are use.
  const data = useQuery((how) => widgets && widgets.length > 0
    ? api.POST("/dashboard/data", { params: { query, header: unattended(how) }, body: { widgets: refs } })
    : SKIP, [key, refsKey]);
  // A refresh is a reload, not a new key: the figures on screen stay until the
  // new ones land. Keyed on a counter it dropped them, and every widget went to
  // its skeleton for the round trip on each cadence tick and each return to the
  // tab (#1049). Another scope or another set of widgets still drops them.
  const refresh = data.reload;
  const answer = data.status === "error" ? data.problem : data.data;
  // The change stream (ZENITH.md §18; ADR-0240 decisions 1d, 2a and 3a): one
  // stream per tab, on which the server announces that a cell's copy moved or
  // a cached reading was refilled, and this page re-reads through the call
  // above. What travels is a signal and never a figure; the scope is the
  // dashboard's, so the server announces only the cells this page's sites sit
  // on, and re-checks that before every message. The timer further down is
  // the fallback while the stream is down (3a): while it is open nothing here
  // asks on a cadence.
  //
  // One re-read per announcement, coalesced: an announcement that arrives
  // while a read is out marks the figures dirty, and the answer's arrival
  // re-reads once more — so the burst of refills that lands behind one copy
  // move costs one read in flight and one pending, never one per
  // announcement. A hidden tab takes no re-read; `onShow` below reads on the
  // next look, as it did before the stream (#1049). A stream that reopens
  // after dropping re-reads once, because an announcement may have passed
  // while it was down. No `EventSource` — the test environment, an old
  // browser — is a stream that is down, and the page polls as it always has.
  const [streamOpen, setStreamOpen] = useState(false);
  // Whether a read is out, as the stream's listener sees it: a ref, because
  // the listener is the browser's and not a render's.
  const reading = useRef(false);
  useEffect(() => { reading.current = data.status === "loading"; }, [data.status]);
  const pendingRead = useRef(false);
  const hasWidgets = !!widgets && widgets.length > 0;
  // The scope as the stream's query string — a string, so the stream is
  // reopened when the scope's ids change and not when the scope object does.
  const search = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) if (v) search.set(k, v);
  const streamSearch = search.toString();
  useEffect(() => {
    if (!hasWidgets || typeof EventSource === "undefined") return;
    const stream = new EventSource(streamSearch ? CHANGES + "?" + streamSearch : CHANGES);
    let dropped = false;
    stream.onopen = () => { setStreamOpen(true); if (dropped) { dropped = false; refresh(UNATTENDED); } };
    stream.onerror = () => { dropped = true; setStreamOpen(false); };
    stream.addEventListener("change", () => {
      if (document.hidden) return;
      if (reading.current) { pendingRead.current = true; return; }
      refresh(UNATTENDED);
    });
    return () => { stream.close(); setStreamOpen(false); };
  }, [streamSearch, hasWidgets, refresh]);
  // The read an announcement asked for while another was out.
  useEffect(() => {
    if (data.status === "loading" || !pendingRead.current) return;
    pendingRead.current = false;
    refresh(UNATTENDED);
  }, [data.status, answer, refresh]);
  // The answer's arrival arms the timer, not the figures' identity. A reload
  // that fails keeps `data.data` (hooks.ts), so keyed on that alone a refused
  // refresh armed nothing: a timer already pending fired once and the polling
  // ended until a click or a tab show (#1310).
  // Every answer is a new object — a fresh `data` on a success, a fresh
  // `problem` on a failure (problem.ts makes one per answer) — and that is the
  // key. `status` alone is not: a reload answered inside one task is batched
  // with its own `loading` transition, so the status reads `error` before and
  // after and nothing changed. It is still read, because a `loading` render
  // that does happen clears the timer: one per answer, none while a request
  // is out. The cadence is the last good answer's, which a failure keeps; a
  // page with no answer has none and arms nothing — the server states it
  // (ZENITH.md §29), and nothing here invents one. While the change stream
  // above is open the timer is not armed at all (ADR-0240 3a): the server
  // says when to re-read, and the cadence is what the page falls back to the
  // moment the stream drops.
  useEffect(() => {
    const after = data.data?.refresh_after_seconds;
    if (!after) return;
    // A hidden tab waits for its next look rather than reading the controller
    // all night.
    const onShow = () => { if (!document.hidden) refresh(); };
    document.addEventListener("visibilitychange", onShow);
    if (data.status === "loading" || streamOpen) return () => document.removeEventListener("visibilitychange", onShow);
    const id = setTimeout(() => { if (!document.hidden) refresh(UNATTENDED); }, after * 1000);
    return () => { clearTimeout(id); document.removeEventListener("visibilitychange", onShow); };
  }, [data.status, data.data, answer, refresh, streamOpen]);
  // How old the figures are is the footer's to say (#1315, `shell/freshness.tsx`):
  // the oldest reading they were composed from, or the answer's own time where
  // it read nothing from a cell — figures composed from Zenith's store at that
  // moment are exactly that old. The page keeps reporting the figures it holds
  // through a refresh, so the age keeps ageing there until the new answer lands.
  useReportedAge(ageOf("figures", data.data?.data_as_of ?? data.data?.generated_at, data.data?.generated_at));
  // When the last refresh failed, the moment its answer landed, said in the
  // head; the age of the figures that stayed is the footer's. Taken when the
  // answer lands, because a clock in render is impure; cleared by the next
  // answer that is not a failure.
  const [failedAt, setFailedAt] = useState<string>();
  useEffect(() => {
    if (data.status === "loading") return;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- records when the answer landed; the status it follows is the fetch's
    setFailedAt(data.status === "error" ? new Date().toISOString() : undefined);
  }, [data.status, answer]);

  const add = (e: Entry) => {
    if (!widgets) return;
    const at = firstFree(widgets, e.w, e.h, columns);
    // Unique within the layout, derived from it: a clock in render is impure,
    // and a counter would restart at 1 and collide with a saved layout's ids.
    const taken = new Set(widgets.map((w) => w.id));
    let n = 1;
    while (taken.has(e.type + "-" + n)) n++;
    const id = e.type + "-" + n;
    change([...widgets, { id, type: e.type, x: at.x, y: at.y, w: e.w, h: e.h }]);
  };
  const remove = (id: string) => widgets && change(widgets.filter((w) => w.id !== id));
  const pin = (id: string, to: WidgetScope | undefined) => widgets && change(widgets.map((w) => (w.id === id ? { ...w, scope: to } : w)));
  const reset = useMutation();
  const doReset = async () => {
    clearTimeout(pending.current);
    const ok = await reset.run(() => api.DELETE("/dashboard/layout", { params: { query: { ...query, neutral }, header: { "Idempotency-Key": idempotencyKey() } } })
      .then((r) => { if (r.data) { setWidgets(r.data.widgets); setDirty(0); setSaveState("idle"); } return r; }));
    if (ok) layout.reload();
  };

  // The briefing (ZENITH.md §16): asked for by the person, over the figures
  // this page shows, and dropped when the scope or the widget set moves on,
  // since it no longer describes what is on screen. It is not offered on a
  // deployment naming no language-model provider, where its only answer would
  // be `no_model_provider` (#2096; /me's `ai_assistant`), and while the
  // feature is unfinished it is offered to a superuser alone (#2106).
  const briefing = useBriefing();
  const [brief, setBrief] = useState<{ key: string; answer?: components["schemas"]["Briefing"] }>();
  const shown = brief && brief.key === key + refsKey ? brief : undefined;
  const mayBrief = !!me?.ai_assistant && showsUnfinished(me) && canAnywhere(me, "ai.use") && widgets !== undefined && widgets.length > 0;
  const askBriefing = async () => {
    const at = key + refsKey;
    setBrief({ key: at });
    const r = await briefing.run(query, refs);
    setBrief({ key: at, answer: r.ok ? r.data : undefined });
  };
  const closeBriefing = () => { setBrief(undefined); briefing.clear(); };

  const audit = useQuery(() => canAnywhere(me, "audit.read")
    ? api.GET("/audit", { params: { query: { page_size: 10 } } })
    : SKIP, [me?.user.id]);

  const present = new Set((widgets ?? []).map((w) => w.type));
  const addable = catalog.filter((c) => !present.has(c.type));
  const pinnedWord = (w: Widget) => w.scope === undefined ? undefined : w.scope.site_id ? "pinned to a site" : w.scope.customer_id ? "pinned to a customer" : w.scope.org_id ? "pinned to an organization" : "pinned to everything";

  return (
    <div ref={page}>
      <div className="page-head">
        <div>
          <h1>Overview</h1>
          <div className="page-sub">
            {widgets && widgets.length === 0 ? <>Nothing on the dashboard for {scopeWord(query)}</>
              : data.data
              ? <>{query.site_id ? "This site" : <>{data.data.site_count} {plural(data.data.site_count, "site")} in {scopeWord(query)}</>}
                {/* When the answer was composed and how old its figures are left this line for the
                    footer (#1315); what stays is the page's own sentence about a refused refresh (#1310). */}
                {/* Restates ADR-0240 decision 3 (3a) — after a refused refresh the page keeps asking on
                    its own: on the server's announcement while its change stream is open, and at
                    refresh_after_seconds while the stream is down. */}
                {data.status === "error" && <> · <span title="The figures shown are the ones last read; the page asks again on its own.">the refresh{failedAt && <> at <time dateTime={failedAt}>{clock(failedAt)}</time></>} failed</span></>}</>
              : "…"}
          </div>
        </div>
        <div className="page-actions">
          {saveState !== "idle" && <span className="save-state" role="status">{saveState === "saving" ? "Saving layout…" : "Layout saved"}</span>}
          <button type="button" className="btn btn-sm" onClick={refresh}>Refresh</button>
          {mayBrief && <button type="button" className="btn btn-sm" disabled={briefing.busy} onClick={() => void askBriefing()}>Brief me</button>}
          {widgets && (
            <button type="button" className={"btn btn-sm" + (editing ? " on" : "")} disabled={!wide} aria-pressed={editing} onClick={() => setEditing((v) => !v)}
              title={wide ? undefined : `The window is too narrow to arrange the dashboard; widen it past ${MIN_GRID_WIDTH}px.`}>
              {editing ? "Done" : "Edit layout"}
            </button>
          )}
        </div>
      </div>
      {layout.status === "error" && <ProblemBox problem={layout.problem} />}
      {save.problem && <ProblemBox problem={save.problem} onDismiss={save.clear} />}
      {reset.problem && <ProblemBox problem={reset.problem} onDismiss={reset.clear} />}
      {data.status === "error" && <ProblemBox problem={data.problem} />}
      {data.data && data.data.warnings.length > 0 && (
        <div role="status" className="notice">{data.data.warnings.map((w) => <div key={w}>{w}</div>)}</div>
      )}
      {editing && widgets && (
        <div className="panel" role="region" aria-label="Edit layout">
          <div className="edit-bar">
            <span className="label" style={{ margin: 0 }}>Add a widget</span>
            {addable.length === 0 && <span className="gallery-empty">Every widget is already on this dashboard.</span>}
            {addable.map((e) => <button key={e.type} type="button" className="btn btn-sm" title={e.description} onClick={() => add(e)}>+ {e.name}</button>)}
            <span style={{ flex: 1 }} />
            {layout.data && !layout.data.is_default && <button type="button" className="btn btn-sm" disabled={reset.busy} onClick={() => void doReset()}>Reset to default</button>}
          </div>
          <div className="edit-hint">Drag a widget by its title to move it, or pull the bottom-right corner to resize. Arrow keys move a focused title; Shift with an arrow resizes. Changes save on their own.</div>
        </div>
      )}
      {shown && <BriefingCard briefing={shown.answer} busy={briefing.busy} problem={briefing.problem} onDismiss={closeBriefing} />}
      {!wide && widgets && widgets.length > 0 && <p className="dim small">Shown as saved. Widen the window past {MIN_GRID_WIDTH}px to rearrange the dashboard.</p>}
      {!widgets && layout.status === "loading" && <Loading label="Loading your dashboard" />}
      {widgets && widgets.length === 0 && <div className="empty"><p className="empty-title">This dashboard is empty.</p><p>Use <b>Edit layout</b> to add widgets.</p></div>}
      {widgets && widgets.length > 0 && (
        <Grid items={widgets} columns={columns} editing={editing} minSize={(w) => ({ w: entry(w.type)?.min_w ?? 1, h: entry(w.type)?.min_h ?? 1 })} onChange={change}
          title={(w) => entry(w.type)?.name ?? w.type}
          subtitle={(w) => pinnedWord(w)}
          bodyClass={(w) => CENTERED.has(w.type) && w.h <= 2 ? "centered" : undefined}
          actions={(w) => <WidgetMenu widget={w} name={entry(w.type)?.name ?? w.type} page={query} onPin={(to) => pin(w.id, to)} onRemove={() => remove(w.id)} />}>
          {(w) => <Boundary resetKey={data.data} fallback={(f) => <WidgetFailed {...f} />}><WidgetBody result={data.data?.widgets[w.id]} /></Boundary>}
        </Grid>
      )}
      {canAnywhere(me, "audit.read") && (
        <section className="recent">
          <h2>Recent changes</h2>
          {audit.status === "loading" && !audit.data && <><Loading srOnly /><TableSkeleton label="Recent changes" head={RECENT_HEAD} /></>}
          {audit.status === "error" && <ProblemBox problem={audit.problem} />}
          {audit.data && audit.data.items.length === 0 && <p className="muted">Nothing has changed yet.</p>}
          {audit.data && audit.data.items.length > 0 && (
            <div className="table-wrap"><table aria-label="Recent changes">
              <thead><tr>{RECENT_HEAD}</tr></thead>
              <tbody>{audit.data.items.map((e) => (
                <tr key={e.id}><td><When value={e.created_at} /></td><td><AuditActionCell action={e.action} /></td><td><TargetRef event={e}><AuditTargetCell event={e} /></TargetRef></td><td><AuditActorCell event={e} /></td></tr>
              ))}</tbody>
            </table></div>
          )}
          <p style={{ marginTop: "var(--sp-3)" }}><ScopeLink to="/audit">All audit events</ScopeLink></p>
        </section>
      )}
    </div>
  );
}

/** Recent changes' header row, once for the table and for the skeleton drawn
 * before the first answer (#1321), so the two cannot differ. */
const RECENT_HEAD = <><th>When</th><th>Action</th><th>Target</th><th>Actor</th></>;

/** Whether the element `ref` names — the page's own root, which fills the
 * content column — is at least MIN_GRID_WIDTH wide. Where the browser cannot
 * say (no ResizeObserver, as under test), it is assumed wide. */
function useWide(ref: RefObject<HTMLElement | null>): boolean {
  const [wide, setWide] = useState(true);
  useEffect(() => {
    if (typeof ResizeObserver === "undefined") return;
    const el = ref.current;
    if (!el) return;
    const measure = () => setWide(el.getBoundingClientRect().width >= MIN_GRID_WIDTH);
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    measure();
    return () => ro.disconnect();
  }, [ref]);
  return wide;
}

/** Pin a widget to a scope regardless of the scope picker, or let it follow the page. */
function WidgetMenu({ widget, name, page, onPin, onRemove }: { widget: Widget; /** The widget's name, as its card's title says it. */ name: string; page: WidgetScope; onPin(to: WidgetScope | undefined): void; onRemove(): void }) {
  const [open, setOpen] = useState(false);
  const trigger = useRef<HTMLButtonElement>(null);
  const menuId = useId();
  const pinned = widget.scope !== undefined;
  const pageKey = scopeKey(page);
  const pinnedKey = widget.scope ? scopeKey(widget.scope) : undefined;
  const close = useCallback(() => setOpen(false), []);
  // The menu is a Popover, not an absolute child: the widget card is
  // `overflow: hidden` and clipped it at its right edge (#53).
  return (
    <span className="seg">
      <button ref={trigger} type="button" className={"btn btn-quiet" + (pinned ? " on" : "")} title={pinned ? "Pinned to " + pinnedTo(pinnedKey ?? "") : "Follows the page"} onClick={() => setOpen((o) => !o)} aria-expanded={open} aria-controls={menuId}>{pinned ? "Pinned" : "Pin"}</button>
      <button type="button" className="btn btn-quiet" onClick={onRemove} aria-label={"Remove " + name}><Glyph icon={CloseIcon} /></button>
      <Popover id={menuId} open={open} anchor={trigger} onClose={close} label="Scope of this widget">
        <button type="button" className={!pinned ? "on" : ""} onClick={() => { onPin(undefined); close(); }}>Follow the page</button>
        <button type="button" className={pinnedKey === "all" ? "on" : ""} onClick={() => { onPin({}); close(); }}>Everything I can see</button>
        {pageKey !== "all" && <button type="button" className={pinnedKey === pageKey ? "on" : ""} onClick={() => { onPin(page); close(); }}>Pin to {scopeWord(page)}</button>}
      </Popover>
    </span>
  );
}
